(() => {
  'use strict';
  const loadedSections = new WeakSet();
  const activeRequests = new WeakMap();
  const watchedImages = new WeakSet();
  const imageObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (entry.isIntersecting) hydrateImage(entry.target);
    }
  }, { rootMargin: '300px 0px' }) : null;

  function hydrateImage(image) {
    const source = image.getAttribute('data-pawly-src');
    if (!source) return;
    const sources = image.getAttribute('data-pawly-srcset');
    if (sources) image.setAttribute('srcset', sources);
    image.setAttribute('src', source);
    image.removeAttribute('data-pawly-src');
    image.removeAttribute('data-pawly-srcset');
    imageObserver?.unobserve(image);
  }

  function watchImages(root) {
    root.querySelectorAll('img[data-pawly-src]').forEach(image => {
      if (watchedImages.has(image)) return;
      watchedImages.add(image);
      if (imageObserver) imageObserver.observe(image);
      else hydrateImage(image);
    });
  }

  async function loadSection(element) {
    if (!element.isConnected || loadedSections.has(element) || activeRequests.has(element)) return;
    const sectionId = element.getAttribute('data-pawly-pdp-section');
    const outer = document.getElementById('shopify-section-' + sectionId);
    if (!outer) return;
    const controller = new AbortController();
    activeRequests.set(element, controller);
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', sectionId);
      const response = await fetch(url.toString(), { credentials: 'same-origin', signal: controller.signal });
      if (!response.ok) throw new Error('Section unavailable');
      const text = await response.text();
      const documentFragment = new DOMParser().parseFromString(text, 'text/html');
      const replacement = documentFragment.getElementById(outer.id);
      if (!replacement || replacement.querySelector('[data-pawly-pdp-section]') || !replacement.querySelector('.product-v1__carousel')) throw new Error('Incomplete section');
      if (!element.isConnected) return;
      loadedSections.add(element);
      sectionObserver?.unobserve(element);
      outer.innerHTML = replacement.innerHTML;
      watchImages(outer);
    } catch (error) {
      if (error.name !== 'AbortError') element.setAttribute('data-pawly-load-failed', '');
    } finally {
      activeRequests.delete(element);
    }
  }

  const sectionObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => { if (entry.isIntersecting) loadSection(entry.target); });
  }, { rootMargin: '1200px 0px' }) : null;

  function initialize(root) {
    watchImages(root);
    root.querySelectorAll('[data-pawly-pdp-section]').forEach(element => {
      if (element.hasAttribute('data-pawly-watched')) return;
      element.setAttribute('data-pawly-watched', '');
      if (sectionObserver) sectionObserver.observe(element);
      else loadSection(element);
      element.addEventListener('pointerenter', () => loadSection(element), { once: true });
      element.addEventListener('focusin', () => loadSection(element));
    });
  }

  function start() {
    initialize(document);
    const main = document.body;
    if (main) new MutationObserver(records => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches('img[data-pawly-src]')) {
            if (imageObserver) imageObserver.observe(node);
            else hydrateImage(node);
          }
          watchImages(node);
        }
      }
    }).observe(main, { childList: true, subtree: true });
  }

  document.addEventListener('shopify:section:load', event => initialize(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
